import { addDays, lookAxis, lookOf, type SettingLookCode, type SettingLookFamily } from '@gk/contracts'
import { lookForChoice, type TaskTypeRow } from './plan.ts'
import type { HistoryBasis, HistoryKind, HistoryLine } from './settingsHistory.ts'

/** M25-A (docs/M25.md D, rules 1, 4, 5 and 7; ADR-131): THE SETTINGS ASSISTANT — A WRITTEN RULE, NEVER A MODEL (the owner's answer of 2026-10-28, the M18
 *  precedent `rotation.ts`, ADR-108). Pure: no I/O, no network, no clock, no randomness; days arrive as arguments and are compared as ISO text or moved with
 *  `addDays`; the same inputs always give the same proposal, whatever the order of the lines or the areas; ties end on the club's order of its choices.
 *  It is ADVICE, computed where it is shown from what the device already holds (`settingsHistory.ts`) and never stored; nothing becomes real until the planner
 *  applies it as an ordinary `job.update@1` (or a new version of a rule); the server checks it again. A proposal never reads a weather, a forecast, a stress day
 *  or a line of play (rules 5 and 7).
 *
 *  It knows nothing of what the club's words mean (rule 4): it reads only the PICTURE CODE the club gave each choice (its meaning is the library's, fixed in
 *  `@gk/contracts` settingLooks.ts: an axis, a heading, a pattern, a lap's sense, raised or lowered) and the setting's family. A choice with no picture is set
 *  aside and said so; a picture is never guessed from a word. The previous cut on an area is its most recent history line before the day: done (confirmed or
 *  as planned) or planned, and a planned one is always said to be planned.
 *
 *  The steps, in the order they decide (printed on screen word for word from `plan.settingsRule.step.*` — change the code, this header and those texts
 *  together; a test counts them), only the blocks of the families the job's settings use:
 *   direction (dir)
 *    1. the choices that have a direction picture, except those the planner left out here;
 *    2. never the same AXIS as the previous cut on any area of the job; if every choice repeats it somewhere, those that repeat it on the fewest areas;
 *    3. then the axis used longest ago on these areas (never used comes first; with heading pictures the choices themselves are compared, so an axis that
 *       comes back is proposed the other way first: the reverse cut);
 *    4. then the fewest uses on these areas over the last 14 days, done and planned;
 *    5. then the next axis clockwise after the previous cut's (6-12, 7:30-1:30, 3-9, 4:30-10:30) — THE APP'S OWN TIE-BREAK: no source gives this order, and its
 *       printed text says so;
 *    6. then the order of the club's choices.
 *   pattern (pat): the choices with a pattern picture (not left out); never the same pattern as the previous cut on any area (fewest repeats otherwise); the
 *    pattern used longest ago; the fewest uses over 14 days; the order of the club's choices.
 *   clean-up lap (lap): the choices with a lap picture; IN A CYCLE ONLY, a sense not yet paired in this cycle with the direction or pattern this step chose, so
 *    that every pair comes once; a lap the other way from the last lap done or planned on these areas (a cut without a lap is not a lap); "no lap" ONLY when
 *    the planner typed a number of laps per 7 days and it is reached on these areas (nothing is skipped by default); the order of the club's choices. The
 *    printed text gives no wheel-track reason (what the practice says: that belongs to an offset-reel triplex only).
 *   groomers (tool): the job's planned value, unless the planner typed a number of cuts per 7 days with the groomers lowered (a job that planned none and no number
 *    typed: nothing is proposed); then lowered when that number is not reached on these areas over the last 7 days and they were not lowered at the previous cut,
 *    else raised; never lowered on its own. Beside every lowered
 *    choice proposed, a fixed stress caution is printed (Toro's manuals): it is not an input and changes nothing here.
 *  A cycle (`proposeCycle`) chooses each step by the same rule as if the steps before it had been done, from the previous cut before its first day, with no
 *  groomers and no typed number; its default length is the number of combinations (direction or pattern choices with a picture × the lap senses; at most 12).
 *  A series (`proposeSeries`) reads each planned job after the proposals before it in its place. `settingsWarnings` flags a planned job whose direction or pattern
 *  repeats the previous cut — never the groomers, never a lap, never a refusal. */

/** One choice of a setting as the rule reads it: the club's word, its picture (null: none, or not of the setting's family). */
export interface RuleChoice { word: string; code: SettingLookCode | null }
export interface RuleSetting { key: string; label: string; family: SettingLookFamily; choices: RuleChoice[] }

/** The choice settings of a type the rule can read, in the order the type declares them: those with at least one picture; the setting's family is the family of
 *  its pictures (the first one decides; a choice whose picture is of another family is read as having none). Pictures are read through the type's CURRENT looks. */
export function ruleSettings(tt: TaskTypeRow | undefined, lang: string): RuleSetting[] {
  if (!tt) return []
  const l = lang.startsWith('en') ? 'en' : 'fr'
  const out: RuleSetting[] = []
  for (const d of tt.param_defs) {
    if (d.kind !== 'choice') continue
    const looks = (d.choices ?? []).map((word) => ({ word, code: lookForChoice(tt, d.key, word) }))
    const first = looks.find((c) => c.code !== null)
    if (!first) continue
    const family = lookOf(first.code!)!.family
    out.push({ key: d.key, label: d.label[l], family, choices: looks.map((c) => ({ word: c.word, code: c.code !== null && lookOf(c.code)!.family === family ? c.code : null })) })
  }
  return out
}

/** The steps as i18n keys, per family, in the order they decide. */
export const RULE_STEPS = {
  dir: ['pool', 'notLast', 'longest', 'fewest', 'clockwise', 'order'].map((s) => `plan.settingsRule.step.dir.${s}`),
  pat: ['pool', 'notLast', 'longest', 'fewest', 'order'].map((s) => `plan.settingsRule.step.pat.${s}`),
  lap: ['pool', 'pair', 'otherWay', 'none', 'order'].map((s) => `plan.settingsRule.step.lap.${s}`),
  tool: ['plan', 'lower'].map((s) => `plan.settingsRule.step.tool.${s}`),
} as const satisfies Record<SettingLookFamily, readonly string[]>
const FAMILY_ORDER: readonly SettingLookFamily[] = ['dir', 'pat', 'lap', 'tool']
/** The keys of the blocks to print for these settings: only the families they use, in the order direction, pattern, lap, groomers. */
export const ruleStepKeys = (settings: ReadonlyArray<Pick<RuleSetting, 'family'>>): string[] =>
  FAMILY_ORDER.filter((f) => settings.some((s) => s.family === f)).flatMap((f) => [...RULE_STEPS[f]])

/** What the planner left out for this proposal: `leaveKey(setting key, the club's word)`. */
export const leaveKey = (key: string, word: string): string => `${key}\u0000${word}`

/** The previous cut, or the last use of a choice: its day, whether it was done or planned, on what basis, and on how many of the areas. */
export interface Cut { day: string; kind: HistoryKind; basis: HistoryBasis; areas: number }
export type StepId =
  | 'only' | 'notLast' | 'longest' | 'fewest' | 'clockwise' | 'order' | 'pair' | 'otherWay' | 'noLapLimit'
  | 'keepPlan' | 'lowerDue' | 'toolPrevLowered' | 'toolLimit'
export interface Reason {
  step: StepId
  /** the previous cut the step compares with (the last lap for the lap's steps); null when there is none */
  prev: Cut | null
  /** the last time THIS choice was used on these areas; null when never */
  last: Cut | null
  /** its uses over the last 14 days (direction and pattern) */
  uses: number
  /** the laps or lowered cuts counted over the last 7 days, and the planner's number (the lap's and the groomers' number steps) */
  count: number | null
  limit: number | null
}
export interface Proposed { word: string; code: SettingLookCode; reason: Reason; stress: boolean }
export type AsideStep = 'noLook' | 'leftOut' | 'repeat' | 'noLapNumber' | 'noLapNotReached' | 'ranked'
export interface SetAside { word: string; code: SettingLookCode | null; step: AsideStep; cut: Cut | null; count: number | null; limit: number | null }
export interface SettingProposal { key: string; label: string; family: SettingLookFamily; picks: Proposed[]; setAside: SetAside[] }
export interface SettingsProposal { settings: SettingProposal[] }

export interface RuleInputs {
  /** the site-local day the proposal is FOR: only cuts before it are read */
  target: string
  areaIds: readonly string[]
  settings: readonly RuleSetting[]
  /** the job's planned values (key → word): the groomers keep it */
  planned: Readonly<Record<string, string | number>>
  lines: readonly HistoryLine[]
}
export interface RuleOptions {
  leave?: ReadonlySet<string>
  /** laps per 7 days: «no lap» is proposed only when this many are reached; empty, zero or not a number: never */
  lapsPerWeek?: number | null
  /** cuts per 7 days with the groomers lowered; empty, zero or not a number: the planned value is kept */
  groomersPerWeek?: number | null
}

const USE_DAYS = 14
const WEEK_DAYS = 7
const MAX_CYCLE = 12
const AXIS_STEP = 180
const cmp = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)
const numberOf = (n: number | null | undefined): number | null => (typeof n === 'number' && Number.isFinite(n) && n >= 1 ? Math.floor(n) : null)

interface Ctx {
  areas: readonly string[]
  target: string
  /** lines before the target, on the job's areas, newest day first (then area, then job) */
  lines: readonly HistoryLine[]
  planned: Readonly<Record<string, string | number>>
  leave: ReadonlySet<string>
  lapsPerWeek: number | null
  groomersPerWeek: number | null
  /** a cycle's state: the pairs already come and the primary choice of this step */
  cycle: { used: ReadonlySet<string>; primary: string } | null
}

const cutOf = (l: HistoryLine, lines: readonly HistoryLine[], key: string): Cut => ({ day: l.day, kind: l.kind, basis: l.basis, areas: new Set(lines.filter((x) => x.day === l.day && x.codes[key] !== undefined).map((x) => x.areaId)).size })
const codeOn = (l: HistoryLine, key: string): SettingLookCode | undefined => l.codes[key]

/** the lines of one setting, newest day first */
const linesOf = (ctx: Ctx, key: string): HistoryLine[] => ctx.lines.filter((l) => codeOn(l, key) !== undefined)

/** the latest line per area */
function prevPerArea(ls: readonly HistoryLine[]): Map<string, HistoryLine> {
  const m = new Map<string, HistoryLine>()
  for (const l of ls) if (!m.has(l.areaId)) m.set(l.areaId, l)
  return m
}

interface Cand { choice: RuleChoice; code: SettingLookCode; order: number }
function split(setting: RuleSetting, ctx: Ctx): { cands: Cand[]; aside: SetAside[] } {
  const cands: Cand[] = [], aside: SetAside[] = []
  setting.choices.forEach((c, order) => {
    if (c.code === null) aside.push({ word: c.word, code: null, step: 'noLook', cut: null, count: null, limit: null })
    else if (ctx.leave.has(leaveKey(setting.key, c.word))) aside.push({ word: c.word, code: c.code, step: 'leftOut', cut: null, count: null, limit: null })
    else cands.push({ choice: c, code: c.code, order })
  })
  return { cands, aside }
}

const reason = (step: StepId, p: Partial<Reason> = {}): Reason => ({ step, prev: null, last: null, uses: 0, count: null, limit: null, ...p })
const pickOf = (c: Cand, r: Reason): Proposed => ({ word: c.choice.word, code: c.code, reason: r, stress: lookOf(c.code)?.family === 'tool' && (lookOf(c.code) as { working: boolean }).working })

/** Cuts the ranked list into the first choice and up to two alternatives; the rest is set aside as ranked. */
function finish(ranked: Array<{ c: Cand; r: Reason }>, aside: SetAside[]): { picks: Proposed[]; setAside: SetAside[] } {
  const picks = ranked.slice(0, 3).map(({ c, r }) => pickOf(c, r))
  const rest = ranked.slice(3).map(({ c }): SetAside => ({ word: c.choice.word, code: c.code, step: 'ranked', cut: null, count: null, limit: null }))
  return { picks, setAside: [...aside, ...rest] }
}

type Crit = { id: StepId; value: (x: Cand) => number | string }
/** Orders by the criteria and names, per pick, the first criterion at which it differs from at least one of the choices that come after it (the one that
 *  actually separated it); the last pick takes the step of the one before it; a lone choice is `only`. */
function rank(cands: readonly Cand[], crits: readonly Crit[], fallback: StepId): Array<{ c: Cand; step: StepId }> {
  const sorted = [...cands].sort((a, b) => {
    for (const k of crits) { const x = k.value(a), y = k.value(b); if (x !== y) return (x as string) < (y as string) ? -1 : 1 }
    return a.order - b.order
  })
  const out: Array<{ c: Cand; step: StepId }> = []
  sorted.forEach((c, i) => {
    const rest = sorted.slice(i + 1)
    if (rest.length === 0) { out.push({ c, step: i === 0 ? fallback : out[i - 1]!.step }); return }
    const k = crits.find((x) => rest.some((o) => x.value(o) !== x.value(c)))
    out.push({ c, step: k ? k.id : 'order' })
  })
  return out
}

/** direction and pattern: the shared pipeline */
function decideShape(setting: RuleSetting, ctx: Ctx): { picks: Proposed[]; setAside: SetAside[] } {
  const { cands, aside } = split(setting, ctx)
  const key = setting.key
  const isDir = setting.family === 'dir'
  const L = linesOf(ctx, key)
  const prev = prevPerArea(L)
  const hasHeading = isDir && setting.choices.some((c) => c.code !== null && (lookOf(c.code) as { heading: number | null }).heading !== null)
  const same = (a: SettingLookCode, b: SettingLookCode): boolean => (isDir ? lookAxis(a) === lookAxis(b) : a === b)
  const ident = (code: SettingLookCode): string => (isDir && !hasHeading ? `axis:${lookAxis(code)}` : code)
  const overall = L[0]
  const overallCut = overall ? cutOf(overall, L, key) : null
  const cutoff = addDays(ctx.target, -USE_DAYS)

  // step 2: never the same axis (or pattern) as the previous cut on any area; if all repeat somewhere, the fewest repeats
  const repeats = new Map<Cand, { n: number; line: HistoryLine | null }>()
  for (const c of cands) {
    let n = 0, newest: HistoryLine | null = null
    for (const a of ctx.areas) { const p = prev.get(a); if (p && same(codeOn(p, key)!, c.code)) { n++; if (!newest || p.day > newest.day) newest = p } }
    repeats.set(c, { n, line: newest })
  }
  const min = cands.length ? Math.min(...cands.map((c) => repeats.get(c)!.n)) : 0
  const kept = cands.filter((c) => repeats.get(c)!.n === min)
  for (const c of cands) {
    const r = repeats.get(c)!
    if (r.n > min) aside.push({ word: c.choice.word, code: c.code, step: 'repeat', cut: r.line ? { day: r.line.day, kind: r.line.kind, basis: r.line.basis, areas: r.n } : null, count: null, limit: null })
  }
  const lastOf = (c: Cand): HistoryLine | undefined => L.find((l) => ident(codeOn(l, key)!) === ident(c.code))
  const usesOf = (c: Cand): number => L.filter((l) => l.day >= cutoff && ident(codeOn(l, key)!) === ident(c.code)).length
  // the clockwise step needs ONE previous axis: when the newest day's cuts carry several (a1 at 0, a2 at 90), it is skipped and the club's order decides,
  // never the axis of whichever area happens to sort first
  const newestAxes = new Set(overall ? L.filter((l) => l.day === overall.day).map((l) => lookAxis(codeOn(l, key)!)) : [])
  const prevAxis = overall && newestAxes.size === 1 ? lookAxis(codeOn(overall, key)!) : null
  const crits: Crit[] = [
    { id: 'longest', value: (c) => lastOf(c)?.day ?? '' },
    { id: 'fewest', value: (c) => usesOf(c) },
    ...(isDir && prevAxis !== null ? [{ id: 'clockwise' as const, value: (c: Cand) => { const d = (((lookAxis(c.code) ?? 0) - prevAxis) % AXIS_STEP + AXIS_STEP) % AXIS_STEP; return d === 0 ? AXIS_STEP : d } }] : []),
  ]
  const alone: StepId = aside.some((a) => a.step === 'repeat') ? 'notLast' : 'only'
  const ranked = rank(kept, crits, alone).map(({ c, step }) => {
    const last = lastOf(c)
    return { c, r: reason(step, { prev: overallCut, last: last ? cutOf(last, L.filter((l) => ident(codeOn(l, key)!) === ident(c.code)), key) : null, uses: usesOf(c) }) }
  })
  return finish(ranked, aside)
}

const senseOf = (code: SettingLookCode): 'cw' | 'ccw' | 'any' | 'none' => (lookOf(code) as { sense: 'cw' | 'ccw' | 'any' | 'none' }).sense

/** the clean-up lap */
function decideLap(setting: RuleSetting, ctx: Ctx): { picks: Proposed[]; setAside: SetAside[] } {
  const { cands, aside } = split(setting, ctx)
  const key = setting.key
  const L = linesOf(ctx, key)
  const isLap = (code: SettingLookCode): boolean => senseOf(code) !== 'none'
  const laps = cands.filter((c) => isLap(c.code))
  const nones = cands.filter((c) => !isLap(c.code))
  const weekCut = addDays(ctx.target, -WEEK_DAYS)
  const perArea = new Map<string, number>()
  for (const l of L) if (l.day >= weekCut && isLap(codeOn(l, key)!)) perArea.set(l.areaId, (perArea.get(l.areaId) ?? 0) + 1)
  const count = Math.max(0, ...[...perArea.values()])
  const limit = ctx.lapsPerWeek
  const reached = limit !== null && count >= limit
  const lastLap = L.find((l) => { const s = senseOf(codeOn(l, key)!); return s === 'cw' || s === 'ccw' })
  const lastSense = lastLap ? senseOf(codeOn(lastLap, key)!) : null
  const lastCut = lastLap ? cutOf(lastLap, L, key) : null
  const ranked: Array<{ c: Cand; r: Reason }> = []
  if (reached && nones.length > 0) {
    ranked.push({ c: nones[0]!, r: reason('noLapLimit', { count, limit }) })
    for (const c of nones.slice(1)) aside.push({ word: c.choice.word, code: c.code, step: 'ranked', cut: null, count: null, limit: null })
  } else {
    for (const c of nones) aside.push({ word: c.choice.word, code: c.code, step: limit === null ? 'noLapNumber' : 'noLapNotReached', cut: null, count: limit === null ? null : count, limit })
  }
  const pairKey = (c: Cand): string => `${ctx.cycle?.primary ?? ''}|${c.code}`
  const crits: Crit[] = [
    ...(ctx.cycle ? [{ id: 'pair' as const, value: (c: Cand) => (ctx.cycle!.used.has(pairKey(c)) ? 1 : 0) }] : []),
    { id: 'otherWay', value: (c) => (lastSense && senseOf(c.code) === lastSense ? 1 : 0) },
  ]
  for (const { c, step } of rank(laps, crits, 'only')) ranked.push({ c, r: reason(step, { prev: lastCut, count, limit }) })
  return finish(ranked, aside)
}

/** the groomers */
function decideTool(setting: RuleSetting, ctx: Ctx): { picks: Proposed[]; setAside: SetAside[] } {
  const { cands, aside } = split(setting, ctx)
  const key = setting.key
  const L = linesOf(ctx, key)
  const working = (c: Cand): boolean => (lookOf(c.code) as { working: boolean }).working
  const ups = cands.filter((c) => !working(c)), downs = cands.filter(working)
  const limit = ctx.groomersPerWeek
  const weekCut = addDays(ctx.target, -WEEK_DAYS)
  const perArea = new Map<string, number>()
  for (const l of L) if (l.day >= weekCut && (lookOf(codeOn(l, key)!) as { working: boolean }).working) perArea.set(l.areaId, (perArea.get(l.areaId) ?? 0) + 1)
  const count = Math.max(0, ...[...perArea.values()])
  const overall = L[0]
  const prevLowered = !!overall && (lookOf(codeOn(overall, key)!) as { working: boolean }).working
  const prevCut = overall ? cutOf(overall, L, key) : null
  let first: { c: Cand; r: Reason } | null = null
  if (limit === null) {
    const planned = ctx.planned[key]
    const plan = typeof planned === 'string' ? cands.find((c) => c.choice.word === planned) : undefined
    if (plan) first = { c: plan, r: reason('keepPlan', { prev: prevCut }) }
  } else {
    const wantLower = count < limit && !prevLowered
    const step: StepId = wantLower ? 'lowerDue' : prevLowered ? 'toolPrevLowered' : 'toolLimit'
    const c = (wantLower ? downs[0] ?? ups[0] : ups[0] ?? downs[0])
    if (c) first = { c, r: reason(step, { prev: prevCut, count, limit }) }
  }
  const ranked = first ? [first, ...cands.filter((c) => c !== first!.c).map((c) => ({ c, r: reason('order', { prev: prevCut }) }))] : []
  return finish(ranked, aside)
}

function decide(setting: RuleSetting, ctx: Ctx): SettingProposal {
  const r = setting.family === 'dir' || setting.family === 'pat' ? decideShape(setting, ctx) : setting.family === 'lap' ? decideLap(setting, ctx) : decideTool(setting, ctx)
  return { key: setting.key, label: setting.label, family: setting.family, ...r }
}

function context(inputs: RuleInputs, opts: RuleOptions, cycle: Ctx['cycle']): Ctx {
  const areas = [...new Set(inputs.areaIds)].sort(cmp)
  const set = new Set(areas)
  const lines = inputs.lines.filter((l) => l.day < inputs.target && set.has(l.areaId)).sort((a, b) => cmp(b.day, a.day) || cmp(a.areaId, b.areaId) || cmp(a.jobId, b.jobId))
  return { areas, target: inputs.target, lines, planned: inputs.planned, leave: opts.leave ?? new Set(), lapsPerWeek: numberOf(opts.lapsPerWeek), groomersPerWeek: numberOf(opts.groomersPerWeek), cycle }
}

/** What identifies a pick when pairs are counted in a cycle: the code of the direction or pattern chosen. */
function decideAll(settings: readonly RuleSetting[], ctx: Ctx): SettingProposal[] {
  const out = new Map<string, SettingProposal>()
  let primary = ''
  for (const s of settings) {
    if (s.family !== 'dir' && s.family !== 'pat') continue
    const p = decide(s, ctx); out.set(s.key, p)
    if (p.picks[0]) primary += `${s.key}=${p.picks[0].code};`
  }
  const later = ctx.cycle ? { ...ctx, cycle: { used: ctx.cycle.used, primary } } : ctx
  for (const s of settings) if (!out.has(s.key)) out.set(s.key, decide(s, later))
  return settings.map((s) => out.get(s.key)!)
}

/** The proposal for one job on one day, per setting: the first choice and up to two alternatives, each with its reason, and every choice set aside with why. */
export function proposeSettings(inputs: RuleInputs, opts: RuleOptions = {}): SettingsProposal {
  return { settings: decideAll(inputs.settings, context(inputs, opts, null)) }
}

/** How long a cycle is by default: the direction or pattern choices with a picture × the lap senses (clockwise and anticlockwise count two, a lap whose sense is
 *  not said one, no lap none), at most 12. */
export function defaultCycleLength(settings: readonly RuleSetting[], leave: ReadonlySet<string> = new Set()): { n: number; combos: number; capped: boolean; primary: number; senses: number } {
  const usable = (s: RuleSetting): RuleChoice[] => s.choices.filter((c) => c.code !== null && !leave.has(leaveKey(s.key, c.word)))
  const primary = settings.filter((s) => s.family === 'dir' || s.family === 'pat').reduce((n, s) => n * usable(s).length, 1)
  const lapSetting = settings.find((s) => s.family === 'lap')
  const senses = lapSetting ? Math.max(1, usable(lapSetting).reduce((n, c) => n + (senseOf(c.code!) === 'none' ? 0 : 1), 0)) : 1
  const combos = primary * senses
  return { n: Math.min(combos, MAX_CYCLE), combos, capped: combos > MAX_CYCLE, primary, senses }
}

export interface CycleInputs { start: string; areaIds: readonly string[]; settings: readonly RuleSetting[]; lines: readonly HistoryLine[] }
export interface CycleStep { index: number; picks: Record<string, Proposed>; values: Record<string, string> }

const synthetic = (areas: readonly string[], day: string, jobId: string, props: SettingProposal[]): HistoryLine[] => {
  const codes: Record<string, SettingLookCode> = {}, words: Record<string, string> = {}
  for (const p of props) if (p.picks[0]) { codes[p.key] = p.picks[0].code; words[p.key] = p.picks[0].word }
  return areas.map((areaId): HistoryLine => ({ areaId, day, kind: 'planned', basis: 'planned', jobId, group: 1, at: '', codes, words }))
}

/** A whole cycle of `n` steps for a new version of a rule: each step chosen by the same rule as if the steps before it had been done, from the previous cut
 *  before the first day; no groomers, no typed number (a cycle counts the rule's days, not the calendar's). */
export function proposeCycle(inputs: CycleInputs, n: number, opts: Pick<RuleOptions, 'leave'> = {}): CycleStep[] {
  const settings = inputs.settings.filter((s) => s.family !== 'tool')
  if (settings.length === 0 || !(n >= 1)) return []
  let lines: HistoryLine[] = inputs.lines.filter((l) => l.day < inputs.start)
  const used = new Set<string>()
  const steps: CycleStep[] = []
  for (let i = 0; i < n; i++) {
    const day = addDays(inputs.start, i)
    const ctx = context({ target: day, areaIds: inputs.areaIds, settings, planned: {}, lines }, { ...opts, lapsPerWeek: null, groomersPerWeek: null }, { used, primary: '' })
    const props = decideAll(settings, ctx)
    const picks: Record<string, Proposed> = {}, values: Record<string, string> = {}
    let primary = ''
    for (const p of props) if (p.picks[0]) {
      picks[p.key] = p.picks[0]; values[p.key] = p.picks[0].word
      if (p.family === 'dir' || p.family === 'pat') primary += `${p.key}=${p.picks[0].code};`
    }
    for (const p of props) if (p.family === 'lap' && p.picks[0]) used.add(`${primary}|${p.picks[0].code}`)
    lines = [...lines, ...synthetic(inputs.areaIds, day, `cycle:${i}`, props)]
    steps.push({ index: i, picks, values })
  }
  return steps
}

export interface SeriesJob { id: string; day: string; areaIds: readonly string[]; params: Readonly<Record<string, string | number>> }
/** A series' planned jobs in day order, each proposed by the same rule with the proposals before it read in place of their planned values (and the jobs' own
 *  planned lines left out of the history). The two numbers are typed once for the list. */
export function proposeSeries(base: { settings: readonly RuleSetting[]; lines: readonly HistoryLine[] }, jobs: readonly SeriesJob[], opts: RuleOptions = {}): Array<{ job: SeriesJob; proposal: SettingsProposal }> {
  const sorted = [...jobs].sort((a, b) => cmp(a.day, b.day) || cmp(a.id, b.id))
  const listed = new Set(sorted.map((j) => j.id))
  const kept = base.lines.filter((l) => !(l.kind === 'planned' && listed.has(l.jobId)))
  let extra: HistoryLine[] = []
  const out: Array<{ job: SeriesJob; proposal: SettingsProposal }> = []
  for (const job of sorted) {
    const proposal = proposeSettings({ target: job.day, areaIds: job.areaIds, settings: base.settings, planned: job.params, lines: [...kept, ...extra] }, opts)
    out.push({ job, proposal })
    extra = [...extra, ...synthetic(job.areaIds, job.day, job.id, proposal.settings)]
  }
  return out
}

export interface WarnJob { id: string; day: string; areaIds: readonly string[]; params: Readonly<Record<string, string | number>> }
export interface SettingsWarning {
  jobId: string; day: string; key: string; family: 'dir' | 'pat'; word: string; code: SettingLookCode
  /** the areas on which the planned value repeats the previous cut, with that cut */
  areas: Array<{ areaId: string; prevDay: string; prevKind: HistoryKind; prevBasis: HistoryBasis }>
}
/** The planned jobs whose DIRECTION or PATTERN repeats the previous cut on one of their areas (done, or planned before it). Never the groomers, never a lap (the
 *  club's own plan holds them on purpose); never a refusal. `readsWork` false (a device that does not hold the whole site's finishes) returns nothing. */
export function settingsWarnings(jobs: readonly WarnJob[], history: { lines: readonly HistoryLine[] }, opts: { readsWork: boolean; settings: readonly RuleSetting[] }): SettingsWarning[] {
  if (!opts.readsWork) return []
  const out: SettingsWarning[] = []
  for (const job of jobs) {
    for (const s of opts.settings) {
      if (s.family !== 'dir' && s.family !== 'pat') continue
      const word = job.params[s.key]
      const code = typeof word === 'string' ? s.choices.find((c) => c.word === word)?.code ?? null : null
      if (typeof word !== 'string' || code === null) continue
      const areas: SettingsWarning['areas'] = []
      for (const areaId of [...new Set(job.areaIds)].sort(cmp)) {
        let prev: HistoryLine | null = null
        for (const l of history.lines) {
          if (l.areaId !== areaId || l.day >= job.day || l.jobId === job.id) continue
          const c = l.codes[s.key]
          if (c === undefined) continue
          if (!prev || l.day > prev.day || (l.day === prev.day && l.jobId < prev.jobId)) prev = l
        }
        const pc = prev ? prev.codes[s.key] : undefined
        if (prev && pc !== undefined && (s.family === 'dir' ? lookAxis(pc) === lookAxis(code) : pc === code)) areas.push({ areaId, prevDay: prev.day, prevKind: prev.kind, prevBasis: prev.basis })
      }
      if (areas.length > 0) out.push({ jobId: job.id, day: job.day, key: s.key, family: s.family, word, code, areas })
    }
  }
  return out
}

/** A value of a job the type no longer admits: a choice renamed or removed, or a setting deleted. The server checks the WHOLE params object against the type in
 *  force (`check_params`), so one such value, on any setting, refuses an update with `PARAM_INVALID`: the panel names them and clears them in the same update. */
export interface StaleValue { key: string; label: string; word: string | number; gone: boolean }
export function staleParams(params: Readonly<Record<string, string | number>>, tt: TaskTypeRow | undefined, lang: string): StaleValue[] {
  if (!tt) return []
  const l = lang.startsWith('en') ? 'en' : 'fr'
  const out: StaleValue[] = []
  for (const key of Object.keys(params).sort(cmp)) {
    const def = tt.param_defs.find((d) => d.key === key)
    const word = params[key]!
    if (!def) out.push({ key, label: key, word, gone: true })
    else if (def.kind === 'choice' && !(typeof word === 'string' && (def.choices ?? []).includes(word))) out.push({ key, label: def.label[l], word, gone: false })
  }
  return out
}
/** The params an « Appliquer » sends: the job's own, the ticked values replacing theirs, the cleared keys removed. A key ticked is never cleared. */
export function applyParams(params: Readonly<Record<string, string | number>>, ticked: Readonly<Record<string, string>>, clear: readonly string[]): Record<string, string | number> {
  const out: Record<string, string | number> = {}
  for (const [k, v] of Object.entries(params)) if (!clear.includes(k) || Object.hasOwn(ticked, k)) out[k] = v
  for (const [k, v] of Object.entries(ticked)) out[k] = v
  return out
}
